/**
 * Sidebar navigation component
 */

import { NavLink } from 'react-router-dom';
import { Phone, Users, Calendar, BarChart3, LogOut, Workflow, Flame, Mic, Bot, Link2 } from 'lucide-react';
import { useAuth, useHasDialer } from '@/lib/auth/AuthProvider';
import { auth } from '@/lib/firebase';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { TenantSwitcher } from '@/components/TenantSwitcher';

const navItems = [
  { to: '/dashboard', icon: Phone, label: 'Dialer', dialerOnly: true },
  { to: '/inbound', icon: Calendar, label: 'Inbound' },
  { to: '/warm-leads', icon: Flame, label: 'Warm Leads' },
  { to: '/cold-calling', icon: Users, label: 'Cold Dialing', dialerOnly: true },
  { to: '/sequences', icon: Workflow, label: 'Sequences', dialerOnly: true },
  { to: '/analytics', icon: BarChart3, label: 'Analytics' },
  { to: '/ai-drafts', icon: Bot, label: 'AI Drafts' },
];

export function Navigation() {
  const { user } = useAuth();
  const hasDialer = useHasDialer();

  const handleSignOut = async () => {
    try {
      await auth.signOut();
    } catch (error) {
      console.error('Sign out error:', error);
    }
  };

  const tenantName = user?.tenant?.name || user?.tenant_id || 'BuildGravity';

  return (
    <aside className="w-64 border-r bg-card flex flex-col">
      {/* Header */}
      <div className="p-6 border-b">
        <h1 className="text-xl font-bold">{tenantName}</h1>
        <p className="text-sm text-muted-foreground">{hasDialer ? 'Sales Dialer' : 'Sales CRM'}</p>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-4 space-y-1">
        {navItems.filter((item) => !item.dialerOnly || hasDialer).map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            className={({ isActive }) =>
              cn(
                'flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors',
                isActive
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
              )
            }
          >
            <item.icon className="h-5 w-5" />
            {item.label}
          </NavLink>
        ))}
        {(user?.is_admin || user?.is_super_admin) && (
          <NavLink
            to="/integrations"
            className={({ isActive }) =>
              cn(
                'flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors',
                isActive
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
              )
            }
          >
            <Link2 className="h-5 w-5" />
            Integrations
          </NavLink>
        )}
        {user?.is_super_admin && (
          <NavLink
            to="/voice-agent"
            className={({ isActive }) =>
              cn(
                'flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors',
                isActive
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
              )
            }
          >
            <Mic className="h-5 w-5" />
            Voice Agent
          </NavLink>
        )}
      </nav>

      {/* User section */}
      <div className="p-4 border-t space-y-2">
        <div className="px-3 py-2">
          <p className="text-sm font-medium truncate">{user?.name || user?.email}</p>
          <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
        </div>
        <TenantSwitcher />
        <Button
          variant="ghost"
          className="w-full justify-start"
          onClick={handleSignOut}
        >
          <LogOut className="h-4 w-4 mr-2" />
          Sign Out
        </Button>
      </div>
    </aside>
  );
}
